import { isRecord } from "@/utils/isRecord";

export interface SerializeNodeConfigContextOptions {
  excludeKeys?: string[];
  maxFieldChars?: number;
}

const DEFAULT_MAX_FIELD_CHARS = 4000;
const TRUNCATION_MARKER = " … [truncated]";

type Primitive = string | number | boolean;

type RenderedValue =
  | { kind: "inline"; text: string }
  | { kind: "block"; lines: string[] };

function isPrimitive(value: unknown): value is Primitive {
  return (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  );
}

function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  if (isRecord(value)) return Object.keys(value).length === 0;
  return false;
}

function isTipTapValue(value: unknown): boolean {
  if (Array.isArray(value)) {
    return value.some(
      (item) =>
        isRecord(item) && (item.type === "doc" || Array.isArray(item.content))
    );
  }
  if (!isRecord(value)) return false;
  if (value.type === "doc") return true;
  if (Array.isArray(value.content)) return true;
  return false;
}

function extractTipTapText(value: unknown): string {
  if (Array.isArray(value)) {
    return value
      .map(extractTipTapText)
      .filter((part) => part !== "")
      .join("\n");
  }
  if (!isRecord(value)) return "";
  const parts: string[] = [];
  if (typeof value.text === "string") parts.push(value.text);
  if (Array.isArray(value.content)) {
    const inner = value.content
      .map(extractTipTapText)
      .filter((part) => part !== "");
    parts.push(inner.join(value.type === "doc" ? "\n" : ""));
  }
  return parts.join("");
}

function formatCompact(value: unknown): string {
  if (isPrimitive(value)) return String(value);
  if (Array.isArray(value)) {
    return value
      .map(formatCompact)
      .filter((part) => part !== "")
      .join(", ");
  }
  if (isRecord(value)) {
    return Object.entries(value)
      .map(([key, entry]) => {
        const text = formatCompact(entry);
        return text === "" ? "" : `${key}: ${text}`;
      })
      .filter((part) => part !== "")
      .join("; ");
  }
  return "";
}

function renderNamedItem(item: Record<string, unknown>): string {
  const name = typeof item.name === "string" ? item.name : "";
  const type =
    typeof item.type === "string"
      ? item.type
      : typeof item.field_type === "string"
        ? item.field_type
        : "";
  const description =
    typeof item.description === "string" ? item.description : "";
  let line = `- ${name}`;
  if (type !== "") line += ` (${type})`;
  if (description !== "") line += `: ${description}`;
  return line;
}

function renderArray(value: unknown[]): RenderedValue | null {
  const records = value.filter(isRecord);
  if (
    records.length === value.length &&
    records.every((item) => typeof item.name === "string")
  ) {
    return { kind: "block", lines: records.map(renderNamedItem) };
  }
  if (isTipTapValue(value)) {
    const text = extractTipTapText(value).trim();
    return text === "" ? null : { kind: "inline", text };
  }
  if (value.every(isPrimitive)) {
    return { kind: "inline", text: value.map(String).join(", ") };
  }
  const lines = value
    .map(formatCompact)
    .filter((part) => part !== "")
    .map((part) => `- ${part}`);
  return lines.length === 0 ? null : { kind: "block", lines };
}

function renderObject(value: Record<string, unknown>): RenderedValue | null {
  if (isTipTapValue(value)) {
    const text = extractTipTapText(value).trim();
    return text === "" ? null : { kind: "inline", text };
  }
  const lines: string[] = [];
  for (const [key, entry] of Object.entries(value)) {
    if (isEmptyValue(entry)) continue;
    const text = formatCompact(entry);
    if (text === "") continue;
    lines.push(`${key}: ${text}`);
  }
  return lines.length === 0 ? null : { kind: "block", lines };
}

function renderValue(value: unknown): RenderedValue | null {
  if (isPrimitive(value)) return { kind: "inline", text: String(value) };
  if (Array.isArray(value)) return renderArray(value);
  if (isRecord(value)) return renderObject(value);
  return null;
}

function neutralizeInstructionClosingTags(value: string): string {
  return value.replace(/<\/(node_configuration|additional_context)/gi, "<$1");
}

export function serializeNodeConfigContext(
  configValues: Record<string, unknown>,
  options?: SerializeNodeConfigContextOptions
): string | null {
  const excludeKeys = new Set(options?.excludeKeys ?? []);
  const maxFieldChars = options?.maxFieldChars ?? DEFAULT_MAX_FIELD_CHARS;
  const entries: string[] = [];
  // Key order follows configValues insertion order so identical configs
  // serialize identically (the output feeds LLM cache keys).
  for (const [key, value] of Object.entries(configValues)) {
    if (excludeKeys.has(key)) continue;
    if (isEmptyValue(value)) continue;
    const rendered = renderValue(value);
    if (rendered === null) continue;
    let valueText =
      rendered.kind === "inline"
        ? rendered.text
        : rendered.lines.map((line) => `  ${line}`).join("\n");
    valueText = neutralizeInstructionClosingTags(valueText);
    if (valueText.length > maxFieldChars) {
      valueText = valueText.slice(0, maxFieldChars) + TRUNCATION_MARKER;
    }
    entries.push(
      rendered.kind === "inline"
        ? `${key}: ${valueText}`
        : `${key}:\n${valueText}`
    );
  }
  if (entries.length === 0) return null;
  return `<node_configuration>\n${entries.join("\n")}\n</node_configuration>`;
}
